import { Download, ExternalLink, Globe, MonitorDown, Package, Smartphone, Truck } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, Card, Skeleton } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { formatDate } from '../../lib/intl.js';
import { formatBytes } from '../common/FileUploadDialog.js';

type ReleaseKind = 'terminal' | 'android' | 'mandob' | 'manager';

interface AppDownload {
  kind: ReleaseKind;
  version: string;
  notes: string | null;
  fileName: string;
  fileSize: number;
  createdAt: string;
  /** Relative to the API base — needs the bearer header, so it can't be a plain <a href>. */
  downloadPath: string;
}

interface AppDownloadsResponse {
  terminal: AppDownload | null;
  android: AppDownload | null;
  /** The field rep's app — a DIFFERENT app from the customer one. */
  mandob: AppDownload | null;
  /** The shop-management desktop app: the whole ERP, plus products that work offline. */
  manager: AppDownload | null;
  /** Browser versions of the same apps, for staff who cannot install anything. */
  webApps: { mandob: string; portal: string; storefront: string | null };
  storeLinks: { android: string | null; ios: string | null };
  /** Scan target for a phone: the store listing when the shop has one, else the .apk. */
  androidQr: { url: string; svg: string } | null;
}

/**
 * Settings → Downloads. The two installable apps a shop hands out: the customer
 * Android app and the cashier's Windows sales terminal.
 *
 * Releases are published by the platform provider, so every empty state points
 * staff at them rather than offering an upload they cannot perform.
 */
export function DownloadsPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const [data, setData] = useState<AppDownloadsResponse | null>(null);
  const [busy, setBusy] = useState<ReleaseKind | null>(null);

  useEffect(() => {
    void api()
      .get<AppDownloadsResponse>('/app-downloads')
      .then((r) => setData(r.data))
      .catch(() => toast.error(t('downloads.loadError', { defaultValue: 'Could not load the apps.' })));
  }, [t]);

  /**
   * The endpoint needs the bearer header, so fetch the bytes and hand the
   * browser a blob URL — the same shape every other authenticated download in
   * the app uses.
   */
  const download = async (rel: AppDownload): Promise<void> => {
    setBusy(rel.kind);
    try {
      const r = await api().get(rel.downloadPath, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data as Blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = rel.fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch {
      toast.error(t('downloads.downloadError', { defaultValue: 'The download failed. Try again.' }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-brand/15 text-brand">
          <Download className="h-5 w-5" aria-hidden />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">{t('downloads.title', { defaultValue: 'Downloads' })}</h1>
          <p className="text-sm text-fg-muted">
            {t('downloads.subtitle', { defaultValue: 'Install the customer app on a phone and the sales terminal on a till.' })}
          </p>
        </div>
      </header>

      {data === null ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card><Skeleton className="h-6 w-40" /><Skeleton className="mt-4 h-32 w-full" /></Card>
          <Card><Skeleton className="h-6 w-40" /><Skeleton className="mt-4 h-32 w-full" /></Card>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <AppCard
            icon={<Smartphone className="h-5 w-5" aria-hidden />}
            title={t('downloads.android.title', { defaultValue: 'Customer app (Android)' })}
            description={t('downloads.android.description', { defaultValue: 'The shopping app your customers install — browse the store, order, and track deliveries.' })}
            release={data.android}
            busy={busy === 'android'}
            onDownload={download}
            emptyHint={t('downloads.android.empty', { defaultValue: 'No Android app published yet — ask your provider to publish one.' })}
            downloadLabel={t('downloads.android.action', { defaultValue: 'Download .apk' })}
            extra={
              <>
                {(data.storeLinks.android || data.storeLinks.ios) && (
                  <div className="flex flex-wrap gap-2">
                    {data.storeLinks.android && (
                      <StoreLink href={data.storeLinks.android} label={t('downloads.playStore', { defaultValue: 'Google Play' })} />
                    )}
                    {data.storeLinks.ios && (
                      <StoreLink href={data.storeLinks.ios} label={t('downloads.appStore', { defaultValue: 'App Store' })} />
                    )}
                  </div>
                )}
                {data.androidQr && (
                  <div className="flex items-center gap-3 rounded-lg border border-border p-3">
                    {/* Server-rendered SVG (the API owns the QR library) — scanned
                        straight off the screen onto a phone. */}
                    <div
                      className="h-24 w-24 flex-none rounded bg-white p-1 [&>svg]:block [&>svg]:h-full [&>svg]:w-full"
                      role="img"
                      aria-label={t('downloads.qrAria', { defaultValue: 'QR code for the customer app' })}
                      dangerouslySetInnerHTML={{ __html: data.androidQr.svg }}
                    />
                    <p className="text-xs text-fg-muted">
                      {t('downloads.qrHint', { defaultValue: 'Scan with a phone camera to install the app on that phone.' })}
                    </p>
                  </div>
                )}
              </>
            }
          />

          <AppCard
            icon={<MonitorDown className="h-5 w-5" aria-hidden />}
            title={t('downloads.terminal.title', { defaultValue: 'Sales terminal (Windows)' })}
            description={t('downloads.terminal.description', { defaultValue: 'The cashier till app. It keeps selling while the internet is down and syncs when it returns, then updates itself.' })}
            release={data.terminal}
            busy={busy === 'terminal'}
            onDownload={download}
            emptyHint={t('downloads.terminal.empty', { defaultValue: 'No terminal version published yet — ask your provider to publish one.' })}
            downloadLabel={t('downloads.terminal.action', { defaultValue: 'Download installer' })}
            extra={data.terminal && (
              <p className="text-xs text-fg-muted">
                {t('downloads.terminal.updateHint', { defaultValue: 'Install once per till — after that the terminal updates itself.' })}
              </p>
            )}
          />
        </div>
      )}

      {/* The back-office apps and the browser versions. Kept below the two a shop
          hands out to customers and cashiers, which is the far commoner errand. */}
      {data && (
        <div className="grid gap-4 lg:grid-cols-2">
          <AppCard
            icon={<Package className="h-5 w-5" aria-hidden />}
            title={t('downloads.manager.title', { defaultValue: 'Manager app (Windows)' })}
            description={t('downloads.manager.description', { defaultValue: 'Run the whole system from your PC — and keep adding products, editing prices and counting stock even while the internet is down.' })}
            release={data.manager}
            busy={busy === 'manager'}
            onDownload={download}
            emptyHint={t('downloads.manager.empty', { defaultValue: 'No manager app published yet — ask your provider to publish one.' })}
            downloadLabel={t('downloads.manager.action', { defaultValue: 'Download installer' })}
            extra={data.manager && (
              <p className="text-xs text-fg-muted">
                {t('downloads.manager.updateHint', { defaultValue: 'Install once per PC — after that the app updates itself.' })}
              </p>
            )}
          />

          <AppCard
            icon={<Truck className="h-5 w-5" aria-hidden />}
            title={t('downloads.mandob.title', { defaultValue: 'Field rep app (Android)' })}
            description={t('downloads.mandob.description', { defaultValue: 'For your reps in the field — sell, collect payments and deliver on the road.' })}
            release={data.mandob}
            busy={busy === 'mandob'}
            onDownload={download}
            emptyHint={t('downloads.mandob.empty', { defaultValue: 'No rep app published yet — ask your provider to publish one.' })}
            downloadLabel={t('downloads.mandob.action', { defaultValue: 'Download .apk' })}
          />

          <Card className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Globe className="h-5 w-5 text-brand" aria-hidden />
              {t('downloads.web.title', { defaultValue: 'Open in a browser' })}
            </div>
            <p className="text-xs text-fg-muted">
              {t('downloads.web.description', { defaultValue: 'The same apps without installing anything — useful on a shared or locked-down machine.' })}
            </p>
            <div className="flex flex-col gap-2">
              <WebAppLink href={data.webApps.mandob} label={t('downloads.web.mandob', { defaultValue: 'Field rep app' })} />
              <WebAppLink href={data.webApps.portal} label={t('downloads.web.portal', { defaultValue: 'Customer portal' })} />
              {data.webApps.storefront && (
                <WebAppLink href={data.webApps.storefront} label={t('downloads.web.storefront', { defaultValue: 'Online store' })} />
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

interface AppCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  release: AppDownload | null;
  busy: boolean;
  onDownload: (rel: AppDownload) => Promise<void>;
  emptyHint: string;
  downloadLabel: string;
  extra?: ReactNode;
}

function AppCard({ icon, title, description, release, busy, onDownload, emptyHint, downloadLabel, extra }: AppCardProps): JSX.Element {
  const { t, i18n } = useTranslation('settings');
  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-bg-subtle text-fg-muted">{icon}</div>
        <div className="min-w-0">
          <h2 className="text-base font-semibold">{title}</h2>
          <p className="text-sm text-fg-muted">{description}</p>
        </div>
      </div>

      {release ? (
        <>
          <dl className="grid grid-cols-3 gap-2 rounded-lg bg-bg-subtle p-3 text-sm">
            <Meta label={t('downloads.version', { defaultValue: 'Version' })} value={<span className="font-mono">{release.version}</span>} />
            <Meta label={t('downloads.size', { defaultValue: 'Size' })} value={formatBytes(release.fileSize)} />
            <Meta label={t('downloads.published', { defaultValue: 'Published' })} value={formatDate(release.createdAt, { locale: i18n.language })} />
          </dl>
          {release.notes && <p className="whitespace-pre-line text-sm text-fg-muted">{release.notes}</p>}
          <div>
            <Button onClick={() => void onDownload(release)} loading={busy}>
              <Download className="h-4 w-4" aria-hidden />
              {downloadLabel}
            </Button>
          </div>
        </>
      ) : (
        <p className="rounded-lg border border-dashed border-border p-4 text-sm text-fg-muted">{emptyHint}</p>
      )}

      {extra}
    </Card>
  );
}

function Meta({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd className="truncate font-medium text-fg">{value}</dd>
    </div>
  );
}

function StoreLink({ href, label }: { href: string; label: string }): JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-bg-subtle"
    >
      <ExternalLink className="h-4 w-4" aria-hidden />
      {label}
    </a>
  );
}

/**
 * A link to the browser version of one of the apps.
 *
 * Full width and stacked, unlike the store badges: these are for reading and
 * clicking on a desktop, not scanning from a phone.
 */
function WebAppLink({ href, label }: { href: string; label: string }): JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-bg-subtle"
    >
      <span>{label}</span>
      <span className="flex items-center gap-1.5 text-xs text-fg-muted">
        {/* The scheme is noise on a page full of URLs; the host is the useful part. */}
        <span className="max-w-[220px] truncate" dir="ltr">{href.replace(/^https?:\/\//, '')}</span>
        <ExternalLink className="h-3.5 w-3.5" aria-hidden />
      </span>
    </a>
  );
}
